import React, { Component, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, notification, Form, Space, Select,DatePicker,Tooltip, Modal,Input } from "antd"; // Import Select from antd
import GenericTable from "../components/GenericTable";
import GenericDrawer from "../components/GenericModal";
import GenericViewModal from "../components/GenericViewModal";
import {
  fetchInsurancesRequest,
  createInsuranceRequest,
  updateInsuranceRequest,
  deleteInsuranceRequest,
} from "../redux-saga/actions/insuranceActions";
import { fetchPayersRequest } from "../redux-saga/actions/payerActions"; // Import the action to fetch payers
import { useParams } from "react-router-dom";
import ProjectNavbar from "../components/ProjectNavbar";
import { updateCurrentProjectId } from "../redux-saga/actions/customerActions";
import { fetchPatientsRequest } from "../redux-saga/actions/patientActions";
import moment from "moment";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {IconShieldCheck} from '@tabler/icons-react';

const InsurancePage = () => {
  const dispatch = useDispatch();
  const { projectId } = useParams();
  const payers = useSelector((state) => state.payer.payers?.data);
  const patients = useSelector((state) => state.patient.patients?.data);
  const insurances = useSelector((state) => state.insurance.insurances?.data);
  // console.log("Insurances Data:", insurances);
  const loading = useSelector((state) => state.insurance.loading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState(null);
  const [form] = Form.useForm();
  const [viewingInsurance, setViewingInsurance] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  const metaData = useSelector((state) => state.insurance.insurances?.meta || {});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchInsurancesRequest(projectId,{ page: currentPage, limit: 2, search: searchTerm }));
    dispatch(fetchPayersRequest(projectId));
    dispatch(fetchPatientsRequest(projectId)); // Fetch payers without `id` as an argument if not needed
    dispatch(updateCurrentProjectId(projectId));
  }, [dispatch, projectId,currentPage, searchTerm]);
  
  console.log("patients Data:", patients);

  const handleCreateOrEdit = (values) => {
    const formattedValues = {
      memberId: String(values.memberId),
      insuredName: values.insuredName,
      patientRelation: values.patientRelation,
      insuredAddress: values.insuredAddress,
      city: values.city,
      state: String(values.state),
      telephoneNo: String(values.telephoneNo),
      zipCode: String(values.zipCode),
      insuresPolicyGroupFECANo: String(values.insuresPolicyGroupFECANo),
      insuresDob: values.insuresDob
        ? values.insuresDob.format("YYYY-MM-DD")
        : null,
      payerId: String(values.payerId || ""), // Ensure payerId is included and is a string
      patientId: String(values.patientId || ""), // Ensure patientId is included and is a string
    };

    console.log("Payload Sent:", formattedValues);

    if (editingInsurance) {
      dispatch(
        updateInsuranceRequest(editingInsurance.id, formattedValues, projectId)
      );
    } else {
      dispatch(createInsuranceRequest(formattedValues, projectId));
    }

    notification.success({
      message: "Success",
      description: "Insurance saved successfully.",
    });
    setIsModalVisible(false);
    setEditingInsurance(null);
    form.resetFields();
    dispatch(fetchInsurancesRequest({ page: currentPage, limit: 2, search: searchTerm }));
  };

  const handleView = (record) => {
    const formattedRecord = {
      ...record,
      createdAt: record.createdAt ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss") : null,
      updatedAt: record.updatedAt ? moment(record.updatedAt).format("YYYY-MM-DD HH:mm:ss") : null,
    };
    setViewingInsurance(formattedRecord);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    const recordWithFormattedDate = {
      ...record,
      insuresDob: record.insuresDob ? moment(record.insuresDob, "YYYY-MM-DD") : null, // Update 'dob' to 'insuresDob'
    };
    setEditingInsurance(recordWithFormattedDate);
    setIsModalVisible(true);
    form.setFieldsValue(recordWithFormattedDate);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Confirm",
      content: "Are you sure you want to delete this insurance?",
      onOk: () => {
        dispatch(deleteInsuranceRequest(record.id, projectId));
        notification.success({
          message: "Success",
          description: "Insurance deleted successfully.",
        });
      },
    });
  };

  const columnsConfig = [
    { title: "Member ID", dataIndex: "memberId" },
    { title: "Insured Name", dataIndex: "insuredName",sortable: true },
    { title: "Relationship", dataIndex: "patientRelation" },
    { title: "City", dataIndex: "city" },
    { title: "State", dataIndex: "state" },
    { title: "Telephone No", dataIndex: "telephoneNo" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <EyeOutlined style={{ fontSize: "20px", color: "#1890ff" }} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined style={{ fontSize: "20px", color: "#faad14" }} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined style={{ fontSize: "20px", color: "#f5222d" }} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];
  

  // Define insuranceModalInputs with a Select component for payerId
  const insuranceModalInputs = [
    { label: "Member ID", name: "memberId", type: "text" },
    { label: "Insured Name", name: "insuredName", type: "text" },
    { label: "Relationship", name: "patientRelation", type: "text" },
    { label: "Address", name: "insuredAddress", type: "text" },
    { label: "City", name: "city", type: "text" },
    { label: "State", name: "state", type: "text" },
    { label: "Telephone No", name: "telephoneNo", type: "text" },
    { label: "ZIP Code", name: "zipCode", type: "text" },
    {
      label: "Policy Group No",
      name: "insuresPolicyGroupFECANo",
      type: "text",
    },
    { label: "DOB", name: "insuresDob", type: "date" ,Component:DatePicker},
    {
      label: "Payer ID",
      name: "payerId",
      type: "select", // Indicate that this is a dropdown
      options: payers?.map((payer) => ({
        label: payer.name, // Assuming `payer.name` is the display name
        value: payer.payerId, // Assuming `payer.id` is the identifier
      })),
      rules: [{ required: true, message: "Payer ID is required" }],
    },
    {
      label: "Patient ID",
      name: "patientId",
      type: "select",
      options: patients?.map((patient) => ({
        label: patient.name,
        value: patient.id,
      })),
      rules: [{ required: true, message: "patient ID is required" }],
    },
  ];

  return (
    <div style={{ display: "flex", gap: "10px" }}>
      <ProjectNavbar />
      <div style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <IconShieldCheck style={{ fontSize: "24px", color: "black" }} />
                <h2 style={{ margin: 0 }}>Insurance</h2>
            </div>
           
            <div style={{ display: "flex", justifyContent: "end", marginBottom: 11 }}>
              <Button type="primary" onClick={() => setIsModalVisible(true)}>
                Create Insurance
              </Button>
            </div>
        </div>
        <Input
          placeholder="Search Insurance"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: "16px" }}
        />
        <GenericTable
          data={insurances}
          columnsConfig={columnsConfig}
          loading={loading}
          pagination={{
            current: metaData.page || 1,
            pageSize: metaData.limit || 2,
            total: metaData.total || 0,
             onChange: (page, pageSize) => {
              setCurrentPage(page);
              dispatch(fetchInsurancesRequest({ page, limit: pageSize, search: searchTerm }));
            },
          }}
        />
        {isModalVisible && (
          <GenericDrawer
            isVisible={isModalVisible}
            handleCancel={() => {
              setIsModalVisible(false);
              setEditingInsurance(null);
            }}
            title={
              <span style={{display : "flex",alignItems:"center"}}>
                <IconShieldCheck style={{ color: "white", marginRight: "8px" }} />
                {editingInsurance ? "Edit Insurance" : "Create Insurance"}
              </span>
            }
            inputs={insuranceModalInputs}
            onSubmit={handleCreateOrEdit}
            predefinedData={editingInsurance || {}}
          />
        )}
        {isViewModalVisible && (
          <GenericViewModal
            isVisible={isViewModalVisible}
            handleCancel={() => setIsViewModalVisible(false)}
            title="View Insurance"
            data={viewingInsurance}
            footerButtons={["Close"]}
            onButtonClick={() => setIsViewModalVisible(false)}
          />
        )}
      </div>
    </div>
  );
};

export default InsurancePage;
